"use client"

import React, { createContext, useCallback, useContext, useMemo } from "react"
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@clerk/nextjs"
import { MY_TEAMS_QUERY_KEYS, myTeamsQueryOptionsFor } from "@/entities/team/model/use-my-teams"
import { dataForThisScope, useRouteScope } from "@/lib/api/scoped-query"
import type { TeamsContextType } from "./types"

/**
 * Exported for ONE overrider: the CRM viewer's `ViewAsProvider`, which hands
 * the See as member's teams to the firm's screens it mounts (spec S16,
 * research 07 §1.2 H6). Everything else reads it through `useMyTeamsContext`.
 */
export const TeamsContext = createContext<TeamsContextType | null>(null)

/**
 * Module-level so "no teams yet" has a STABLE identity.
 *
 * This provider memoises its context value specifically to stop an app-wide
 * re-render cascade (see the note on `value` below), and a fresh `[]` per
 * render would defeat that for every state where the list is absent — which,
 * since G23, now includes the whole cross-product hand-over window.
 */
const NO_TEAMS: TeamsContextType["myTeams"] = []

/**
 * TeamsProvider
 *
 * Non-blocking context provider that fetches the current user's teams with
 * their role per team. Unlike IdentityProvider, this does NOT gate rendering
 * — children render immediately and can check `isLoading` themselves.
 *
 * Shares the same TanStack Query cache key as `useMyTeams`, so any component
 * using either approach sees the same data without duplicate network requests.
 *
 * Mount after IdentityProvider and before BillingGate in the layout tree.
 */
export function TeamsProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const { isLoaded, isSignedIn } = useAuth()

  // Only fetch when Clerk is loaded and user is signed in
  const queryEnabled = isLoaded && isSignedIn === true

  // The ROUTE names the product (G23). This provider sits above every
  // `PlatformScopeProvider`, so the scope store would lag a render behind the
  // surface — and a request keyed for one product would carry another's header.
  const scope = useRouteScope()
  const query = useQuery({
    // The SAME options `useMyTeams` builds, imported rather than rewritten:
    // two copies under one key is how the second one gets fixed a release
    // later than the first.
    ...myTeamsQueryOptionsFor(scope, queryEnabled),
    placeholderData: keepPreviousData,
  })
  const { isPending, isFetching, error } = query
  // Held so the list does not blink empty across the flip; never answered with,
  // because the other product's teams belong to another organisation.
  const myTeams =
    dataForThisScope({ data: query.data, isPlaceholderData: query.isPlaceholderData }) ?? NO_TEAMS

  // A disabled query has isPending=true but isFetching=false
  const isLoading = (isPending && isFetching) || query.isPlaceholderData

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: MY_TEAMS_QUERY_KEYS.list(),
    })
  }, [queryClient])

  // Memoize the value so consumers (any `useMyTeamsContext()` caller) only
  // re-render when the underlying data actually changes. Without this, every
  // render of TeamsProvider produces a new object identity and forces every
  // descendant consumer to re-render — a hot path during marquee gestures.
  const value = useMemo(
    () => ({
      myTeams,
      isLoading,
      error: error as Error | null,
      refetch,
    }),
    [myTeams, isLoading, error, refetch],
  )

  return (
    <TeamsContext.Provider value={value}>
      {children}
    </TeamsContext.Provider>
  )
}

/**
 * useMyTeamsContext
 *
 * Access the current user's teams from the TeamsProvider context.
 * Named differently from `useMyTeams` to avoid confusion — the standalone
 * hook is for explicit fetching, this context hook is for components that
 * need app-wide teams data without triggering their own query.
 *
 * Example:
 * ```tsx
 * const { myTeams, isLoading } = useMyTeamsContext()
 * const leadTeams = myTeams.filter(t => t.myRole === "lead")
 * ```
 */
export function useMyTeamsContext(): TeamsContextType {
  const context = useContext(TeamsContext)

  if (!context) {
    throw new Error("useMyTeamsContext must be used within TeamsProvider")
  }

  return context
}
